import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Activity, Beaker, Search, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { cn } from "@/lib/utils";
import { useMemorySearch } from "@/hooks/useMemorySearch";
import type { MemorySearchDocType, MemorySearchResultRow } from "@/types";

/**
 * Experiment Memory Search bar (R9 / Sub-3).
 *
 * Source: docs/engineering/TASKS.md § ENG-R9-MEMORY-SEARCH Sub-3.
 * Spec:   docs/product/experience-and-ui.md § Experiment Memory Search L1289-L1326
 *         + L2028-L2065 (header chrome placement + Cmd/Ctrl+K
 *         shortcut + incremental dropdown + per-row type icon +
 *         primary label + context line + [Open] action; no
 *         AI-suggested chip).
 *
 * Layout:
 * - Persistent input in the global header chrome between Apps tree
 *   and Mode badge.
 * - Cmd+K (macOS) / Ctrl+K (other) focuses the input.
 * - Typing renders incremental results (300ms debounced) in a
 *   dropdown panel below the input.
 * - Each row: Lucide icon (Beaker / Activity / Sparkles) + primary
 *   label + context line + right-aligned [Open] link to the
 *   traceability_link route.
 * - The retrieval surface is read-only -- no AI-suggested chip per
 *   spec L2065 ("pure retrieval; no LLM call; no claim safety
 *   required -- the matched text is verbatim from prior records,
 *   not model-generated").
 */

const ICON_BY_DOC_TYPE: Record<
  MemorySearchDocType,
  typeof Beaker | typeof Activity | typeof Sparkles
> = {
  experiment: Beaker,
  contribution: Activity,
  hypothesis_rationale: Sparkles,
};

function buildDocTypeAria(t: TFunction): Record<MemorySearchDocType, string> {
  return {
    experiment: t("header.memorySearchBar.docTypeExperiment"),
    contribution: t("header.memorySearchBar.docTypeContribution"),
    hypothesis_rationale: t(
      "header.memorySearchBar.docTypeHypothesisRationale",
    ),
  };
}

interface MemorySearchBarProps {
  appId: string | null;
}

export default function MemorySearchBar({ appId }: MemorySearchBarProps) {
  const { t } = useTranslation();
  const docTypeAria = buildDocTypeAria(t);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [rawQuery, setRawQuery] = useState("");
  const [open, setOpen] = useState(false);

  const { data, isFetching, isError } = useMemorySearch({
    appId,
    rawQuery,
    // The hash read waits until the search is opened (focus or ⌘K).
    active: open,
  });

  // Cmd+K / Ctrl+K focuses the input from anywhere in the app shell.
  useEffect(() => {
    function onKeydown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      } else if (event.key === "Escape") {
        setOpen(false);
        inputRef.current?.blur();
      }
    }
    window.addEventListener("keydown", onKeydown);
    return () => window.removeEventListener("keydown", onKeydown);
  }, []);

  // Click-outside closes the dropdown without clearing the input
  // (so the operator can re-focus and resume).
  useEffect(() => {
    function onClick(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, []);

  function handleSelectResult(row: MemorySearchResultRow) {
    setOpen(false);
    navigate(row.traceability_link);
  }

  const results = data?.results ?? [];
  const showDropdown = open && rawQuery.trim().length > 0;
  const showNoMatches =
    showDropdown && !isFetching && !isError && results.length === 0;

  return (
    <div
      ref={containerRef}
      className="relative flex-1 max-w-md"
      role="search"
      aria-label={t("header.memorySearchBar.searchAriaLabel")}
    >
      <div className="relative">
        <Search
          size={14}
          className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted"
          aria-hidden="true"
        />
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-autocomplete="list"
          value={rawQuery}
          onChange={(event) => {
            setRawQuery(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={t("header.memorySearchBar.inputPlaceholder")}
          disabled={appId === null}
          aria-label={t("header.memorySearchBar.inputAriaLabel")}
          aria-expanded={showDropdown}
          aria-controls="memory-search-results"
          className={cn(
            "h-8 w-full rounded-md border border-divider bg-surface-alt pl-8 pr-3 text-sm text-ink",
            "placeholder:text-muted",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500",
            "disabled:cursor-not-allowed disabled:opacity-60",
          )}
        />
      </div>
      {showDropdown && (
        // The results panel owns its width (ENG-APP-UX-TUNEUP C9 W12,
        // pass-3 M3-R28-1): centred on the input and 28rem wide (the bar's
        // own max-w-md ceiling), so at the 1024 px floor -- where the header's
        // clusters leave the input ~208 px -- the rows still read in full and
        // the panel stays inside the viewport with a long app name (measured
        // 432..880 at 1024; identical to the input from 1280 up). It stays a
        // DOM child of the container: the click-outside handler above tests
        // containerRef.contains, so no portal. The 640 px command menu (design
        // spec section 12 section 5 item 7) is its own PO-ordered ticket.
        <div
          id="memory-search-results"
          role="listbox"
          className={cn(
            "absolute left-1/2 top-10 z-50 max-h-96 w-md -translate-x-1/2 overflow-y-auto rounded-md border border-divider bg-surface shadow-lg",
          )}
        >
          {isFetching && (
            <div className="px-3 py-2 text-xs text-muted">
              {t("header.memorySearchBar.searchingLabel")}
            </div>
          )}
          {isError && (
            <div className="px-3 py-2 text-xs text-red-600">
              {t("header.memorySearchBar.searchFailedError")}
            </div>
          )}
          {showNoMatches && (
            <div className="px-3 py-2 text-xs text-muted">
              {t("header.memorySearchBar.noMatches", {
                query: rawQuery.trim(),
              })}
            </div>
          )}
          {results.map((row) => {
            const Icon = ICON_BY_DOC_TYPE[row.doc_type];
            return (
              <button
                key={row.doc_id}
                type="button"
                role="option"
                aria-selected="false"
                onClick={() => handleSelectResult(row)}
                className={cn(
                  "flex w-full items-start gap-3 px-3 py-2 text-left",
                  "hover:bg-divider-soft focus-visible:bg-divider-soft",
                  "focus-visible:outline-none",
                )}
              >
                <Icon
                  size={16}
                  className="mt-0.5 shrink-0 text-muted"
                  aria-label={docTypeAria[row.doc_type]}
                />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-ink">
                    {row.primary_label}
                  </div>
                  <div className="truncate text-xs text-muted">
                    {row.context_line}
                  </div>
                </div>
                <span className="shrink-0 text-xs font-medium text-primary-500">
                  {t("header.memorySearchBar.openLink")}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
